// Types for the new "Spread" strategy feature (NY Spread / SX Spread).
// UI-only for now — no backend/DB exists yet for this feature.
// Kept in a separate file so the existing NSE/BSE types (types/index.ts) are never touched.

export interface SpreadTSLRange {
  entry_start: number;
  entry_peak: number;
  range: number;
}

export type SpreadMarket = 'NIFTY' | 'SENSEX';
export type SpreadDirection = 'BULLISH' | 'BEARISH';
export type SpreadOptionType = 'CE' | 'PE';

// Which leg is placed first, derived from direction + option type:
//   BULLISH + CE -> BUY_FIRST  (debit call spread:  Buy near CE, Sell further CE)
//   BULLISH + PE -> SELL_FIRST (credit put spread:  Sell near PE, Buy further PE)
//   BEARISH + CE -> SELL_FIRST (credit call spread: Sell near CE, Buy further CE)
//   BEARISH + PE -> BUY_FIRST  (debit put spread:   Buy near PE, Sell further PE)
export type SpreadLegOrder = 'BUY_FIRST' | 'SELL_FIRST';

export interface SpreadConfig {
  id: number;
  market: SpreadMarket;
  direction: SpreadDirection;
  option_type: SpreadOptionType;
  name: string;
  expiry_date: string;
  is_active: boolean;
  entry_active?: boolean;
  exit_active?: boolean;

  // Strike selection
  first_leg_offset: number;   // steps away from ATM for the leg placed first (CE steps up, PE steps down)
  second_leg_offset: number;  // additional steps away from the first leg's strike, for the leg placed second (1..5)
  delay_seconds: number;      // gap (seconds) between first leg fill and placing the second leg's order
  // Optional 2nd SELL leg, one step further OTM than the SELL leg ("Sell Offset-2" = second_leg_offset + 1).
  // When enabled, the SELL side's lots split evenly across the two SELL legs; the BUY leg is unaffected.
  third_leg_enabled: boolean;

  // Entry trigger (same X/Y breadth concept as existing NSE/BSE configs)
  x_value: number;
  y_value: number;

  // Sizing — client's explicit ask: replaces the old flat Lots field. Quantity is now derived
  // from this % of the configured Capital Amount (Broker Management) against the SELL leg's
  // LTP, the same formula Nifty Buying already uses.
  capital_percentage: number;
  slippage_percentage: number;

  // Stop Loss
  sl_x: number;
  sl_y: number;
  sl_points: number;   // "NP" — Nifty/Sensex points based SL

  // Target
  target_x: number;
  target_y: number;
  target_points: number; // "NP" — points based target

  // Trailing Stop Loss
  tsl_points: number;    // "NP" — base TSL points value
  tsl_ranges: SpreadTSLRange[];

  created_at?: string;
  broker?: string; // 'DHAN' (default) | 'GROWW' | ... — see Broker Management
}

export const STRIKE_STEP: Record<SpreadMarket, number> = {
  NIFTY: 50,
  SENSEX: 100,
};

export const SPREAD_LOT_SIZE: Record<SpreadMarket, number> = {
  NIFTY: 65,
  SENSEX: 20,
};

// BULLISH+CE / BEARISH+PE -> Buy first. BULLISH+PE / BEARISH+CE -> Sell first.
// (Client briefly asked for "always Buy first", then reverted back to this.)
export function getLegOrder(direction: SpreadDirection, optionType: SpreadOptionType): SpreadLegOrder {
  if (direction === 'BULLISH') return optionType === 'CE' ? 'BUY_FIRST' : 'SELL_FIRST';
  return optionType === 'CE' ? 'SELL_FIRST' : 'BUY_FIRST';
}

// CE strikes step UP away from ATM, PE strikes step DOWN away from ATM.
export function strikeDirectionSign(optionType: SpreadOptionType): 1 | -1 {
  return optionType === 'CE' ? 1 : -1;
}
